<template>
  <div class="file-management">
    <el-card>
      <div slot="header">
        <span>文件管理</span>
        <div style="float: right;">
          <el-radio-group v-model="viewMode" size="small" style="margin-right: 10px;">
            <el-radio-button label="grid">网格视图</el-radio-button>
            <el-radio-button label="list">列表视图</el-radio-button>
          </el-radio-group>
          <el-upload :action="uploadConfig.action" :headers="uploadConfig.headers" :on-success="handleUploadSuccess"
            :on-error="handleUploadError" :show-file-list="false" :multiple="true" style="display: inline-block;">
            <el-button size="small" type="primary" icon="el-icon-upload">上传文件</el-button>
          </el-upload>
        </div>
      </div>

      <!-- 拖拽上传区域 -->
      <div class="upload-dragger" @drop.prevent="handleDrop" @dragover.prevent @dragenter.prevent="dragEnter"
        @dragleave.prevent="dragLeave" :class="{ 'drag-over': isDragOver }">
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip">支持jpg、png、gif、pdf等格式，单个文件不超过20MB</div>
      </div>

      <!-- 网格视图 -->
      <div v-if="viewMode === 'grid'" class="file-grid" v-loading="loading">
        <div v-for="file in files" :key="file.id" class="file-item">
          <div class="file-preview" @click="previewFile(file)">
            <img v-if="isImage(file)" :src="$http.getFullImageUrl(file.fileUrl)" :alt="file.originalName">
            <i v-else class="el-icon-document file-icon"></i>
          </div>
          <div class="file-info">
            <div class="file-name" :title="file.originalName">{{ file.originalName }}</div>
            <div class="file-size">{{ formatFileSize(file.fileSize) }}</div>
          </div>
          <div class="file-actions">
            <el-button size="mini" icon="el-icon-view" @click="previewFile(file)">预览</el-button>
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="deleteFile(file)">删除</el-button>
          </div>
        </div>
      </div>

      <!-- 列表视图 -->
      <el-table v-else :data="files" style="width: 100%; margin-top: 20px;" v-loading="loading">
        <el-table-column label="预览" width="80">
          <template slot-scope="scope">
            <img v-if="isImage(scope.row)" :src="$http.getFullImageUrl(scope.row.fileUrl)" class="table-preview">
            <i v-else class="el-icon-document" style="font-size: 32px; color: #999;"></i>
          </template>
        </el-table-column>
        <el-table-column prop="originalName" label="文件名"></el-table-column>
        <el-table-column label="文件类型" width="120">
          <template slot-scope="scope">
            {{ scope.row.fileType }}
          </template>
        </el-table-column>
        <el-table-column label="文件大小" width="120">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.fileSize) }}
          </template>
        </el-table-column>
        <el-table-column label="上传时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.uploadTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280">
          <template slot-scope="scope">
            <el-button size="mini" icon="el-icon-view" @click="previewFile(scope.row)">预览</el-button>
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="deleteFile(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页区域 -->
      <div class="pagination-container">
        <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
          :page-sizes="[12, 24, 36, 60]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>

    <!-- 预览对话框 -->
    <el-dialog title="文件预览" :visible.sync="previewVisible" width="600px">
      <div class="preview-content">
        <img v-if="isImage(currentPreviewFile)" :src="$http.getFullImageUrl(currentPreviewFile.fileUrl)"
          style="max-width: 100%;">
        <div v-else class="no-preview">
          <i class="el-icon-document" style="font-size: 64px; color: #999;"></i>
          <p>该文件类型不支持预览</p>
          <el-button type="primary" @click="downloadFile(currentPreviewFile)">下载文件</el-button>
        </div>
      </div>
      <div class="preview-info">
        <p><strong>文件名：</strong>{{ currentPreviewFile.originalName }}</p>
        <p><strong>文件大小：</strong>{{ formatFileSize(currentPreviewFile.fileSize) }}</p>
        <p><strong>上传时间：</strong>{{ formatDate(currentPreviewFile.uploadTime) }}</p>
        <p><strong>文件URL:</strong>
          <el-input :value="currentPreviewFile.fileUrl" readonly size="small">
          </el-input>
        </p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'FileManagement',
  data() {
    return {
      files: [],
      loading: false,
      viewMode: 'grid',
      isDragOver: false,
      previewVisible: false,
      currentPreviewFile: {},
      currentPage: 1,
      pageSize: 24,
      total: 0,
    }
  },
  computed: {
    uploadConfig() {
      return this.$http.getUploadConfig()
    }
  },
  mounted() {
    this.loadFiles()
  },
  methods: {
    async loadFiles() {
      this.loading = true
      try {
        const response = await this.$http.get(`/files/page?page=${this.currentPage}&size=${this.pageSize}`)
        if (response.success && response.data) {
          this.files = response.data || []
          this.total = response.total || 0
        }
      } catch (error) {
        this.$message.error('加载文件列表失败')
        console.error('加载文件列表失败:', error)
      } finally {
        this.loading = false
      }
    },

    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.loadFiles()
    },

    handleCurrentChange(val) {
      this.currentPage = val
      this.loadFiles()
    },

    handleUploadSuccess(response) {
      if (response.code === 200) {
        this.$message.success('文件上传成功')
        this.loadFiles()
      } else {
        this.$message.error('文件上传失败: ' + response.message)
      }
    },

    handleUploadError(error) {
      this.$message.error('文件上传失败: ' + error.message)
    },

    dragEnter() {
      this.isDragOver = true
    },

    dragLeave() {
      this.isDragOver = false
    },

    async handleDrop(e) {
      this.isDragOver = false
      const files = e.dataTransfer.files
      if (files.length === 0) return

      for (let i = 0; i < files.length; i++) {
        await this.uploadFile(files[i])
      }
    },

    async uploadFile(file) {
      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await this.$http.post('/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        if (response.success) {
          this.$message.success(`${file.name} 上传成功`)
          this.loadFiles()
        } else {
          this.$message.error(`${file.name} 上传失败: ${response.message}`)
        }
      } catch (error) {
        this.$message.error(`${file.name} 上传失败`)
        console.error('上传失败:', error)
      }
    },

    previewFile(file) {
      this.currentPreviewFile = file
      this.previewVisible = true
    },

    deleteFile(file) {
      this.$confirm(`确定要删除文件"${file.originalName}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await this.$http.delete(`/files/${file.id}`)
          if (response.success) {
            this.$message.success('删除成功')
            this.loadFiles()
          } else {
            this.$message.error('删除失败: ' + response.message)
          }
        } catch (error) {
          this.$message.error('删除失败')
          console.error('删除失败:', error)
        }
      }).catch(() => { })
    },

    downloadFile(file) {
      window.open(this.$http.getFullImageUrl(file.fileUrl), '_blank')
    },

    isImage(file) {
      if (!file || !file.fileType) return false
      return file.fileType.startsWith('image/')
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.file-management {
  max-width: 1400px;
}

.upload-dragger {
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;
}

.upload-dragger:hover,
.upload-dragger.drag-over {
  border-color: #409eff;
  background-color: #f5f7fa;
}

.upload-dragger i {
  font-size: 67px;
  color: #c0c4cc;
}

.el-upload__text {
  color: #606266;
  font-size: 14px;
  margin-top: 10px;
}

.el-upload__text em {
  color: #409eff;
  font-style: normal;
}

.el-upload__tip {
  font-size: 12px;
  color: #999;
  margin-top: 7px;
}

.file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.file-item {
  border: 1px solid #eee;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.3s;
}

.file-item:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.file-preview {
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  cursor: pointer;
}

.file-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.file-icon {
  font-size: 64px;
  color: #999;
}

.file-info {
  padding: 10px;
  background-color: #fff;
}

.file-name {
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 5px;
}

.file-size {
  font-size: 12px;
  color: #999;
}

.file-actions {
  padding: 10px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.table-preview {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.preview-content {
  text-align: center;
  margin-bottom: 20px;
}

.no-preview {
  padding: 40px;
}

.no-preview p {
  margin: 20px 0;
  color: #999;
}

.preview-info {
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.preview-info p {
  margin: 10px 0;
  font-size: 14px;
}
</style>